'use client'

import { useCallback, useState, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import Image from 'next/image';
import { motion, Variants } from 'framer-motion';

import { ParagraphSection } from '@/types/drupal';
import { Button } from '../ui/Button';
import { trackEvent } from '@/lib/analytics';

const textEntry: Variants = {
  hidden: { opacity: 0, y: 15 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.5, ease: "easeOut" } 
  }
};

const imageReveal: Variants = {
  hidden: { scale: 1.1, opacity: 0 },
  visible: { 
    scale: 1, 
    opacity: 1, 
    transition: { duration: 1.2, ease: [0.4, 0, 0.2, 1] } 
  }
};

export default function Hero({ data }: { data: ParagraphSection }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    containScroll: false,
    loop: false,
    duration: 60,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const totalSlides = data.heroItems?.length || 0;

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    queueMicrotask(() => onSelect(emblaApi));
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section 
      id="hero"
      className="Hero relative my-[30px] bg-white overflow-hidden"
      aria-label="Hero"
      role="region"
      aria-roledescription="carousel"
    >
      <div className="section-container pt-2.5 pb-[30px] lg:pb-[70px] lg:pt-[30px] lg:pb-[170px]">

        {/* Live region for slide change announcements */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          Slide {selectedIndex + 1} of {totalSlides}
        </div>

        <div className="relative flex flex-col lg:flex-row lg:grid lg:grid-cols-2 gap-6 lg:gap-[170px] items-center lg:items-end min-h-[auto] lg:min-h-[600px]">
          <div className="Hero__heading relative z-30 w-full flex flex-col justify-start lg:justify-end text-center lg:text-left">
            {data.heroItems?.map((item, index) => {
              const isActive = index === selectedIndex;

              return (
                <motion.div
                  key={index}
                  initial="hidden"
                  animate={isActive ? "visible" : "hidden"}
                  variants={textEntry}
                  className={`
                    left-0 w-full lg:max-w-[426px] transition-all ease-[cubic-bezier(0.2,0,0,1)]
                    ${isActive
                      ? 'relative opacity-100 translate-y-0 visible'
                      : 'absolute top-0 opacity-0 translate-y-12 invisible delay-0'
                    }
                    lg:absolute lg:top-auto lg:bottom-4
                  `}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`Slide ${index + 1} of ${totalSlides}`}
                  aria-hidden={!isActive}
                >
                  <div
                    className="[&_em]:not-italic [&_em]:text-[#0A4492] [&_i]:not-italic [&_i]:text-[#0A4492]"
                    dangerouslySetInnerHTML={{ __html: item.title.processed.replace('<h2', '<h1').replace('</h2>', '</h1>') }}
                  ></div>

                  <div
                    className="mt-8 max-w-[250px] text-center lg:text-left md:max-w-[100%] lg:max-w-[auto] mx-auto lg:ml-0"
                    dangerouslySetInnerHTML={{ __html: item.body?.processed || '' }}
                  ></div>

                  {item.ctaLink && (
                    <motion.div className="mt-10 text-center lg:text-left" variants={textEntry}>
                      <Button
                        label={item.ctaLabel || 'Learn More'}
                        href={item.ctaLink.url}
                        iconRight="ArrowUpRight"
                        onClick={() => trackEvent('cta_click', {
                          cta_label: item.ctaLabel || 'Learn More',
                          cta_location: 'hero',
                          cta_url: item.ctaLink?.url,
                        })}
                      />
                    </motion.div>
                  )}

                  <div className="flex justify-center lg:justify-start gap-3 mt-10 lg:mt-12" role="tablist" aria-label="Slide navigation">
                    {data.heroItems?.map((_, i) => (
                      <button
                        key={i}
                        role="tab"
                        aria-selected={i === selectedIndex}
                        aria-label={`Go to slide ${i + 1} of ${totalSlides}`}
                        onClick={() => {
                          emblaApi?.scrollTo(i);
                          trackEvent('hero_slide', { slide_index: i, slide_direction: 'dot' });
                        }}
                        className={`h-0.5 w-12 transition-all duration-700 cursor-pointer ${i === selectedIndex ? 'bg-black' : 'bg-gray-200'}`}
                      />
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="Hero__media w-full overflow-visible z-10 lg:md-0"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={imageReveal}
            ref={emblaRef}
            aria-hidden="true"
          >
            <div className="flex gap-4">
              {data.heroItems?.map((item, index) => {
                const isActive = index === selectedIndex;
                const isPast = index < selectedIndex;

                return (
                  <div
                    key={index}
                    className="relative min-w-[255px] lg:min-w-[500px]"
                    style={{
                      opacity: isPast ? 0 : 1,
                      transition: 'opacity 0.8s ease-in-out'
                    }}
                  >
                    <div
                      className={`
                        relative h-[325px] lg:h-[635px] w-full overflow-hidden rounded-[2rem] lg:rounded-[2.5rem] transition-all duration-1000 cubic-bezier(0.4, 0, 0.2, 1)
                        ${isActive
                          ? 'scale-100 blur-0 opacity-100 shadow-2xl'
                          : 'scale-90 lg:scale-80 blur-xs lg:blur-md opacity-40 shadow-none'
                        }
                      `}
                    >
                      {item.image?.mediaImage.url && (
                        <Image
                          src={item.image.mediaImage.url}
                          alt={item.image.mediaImage.alt || ''}
                          fill
                          className="object-cover"
                          priority
                          sizes="(max-width: 1024px) 100vw, 600px"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
